import { selectIsPremium } from "domain/auth/auth.selectors";
import {
  HiDownload,
  HiHeart,
  HiLockClosed,
  HiOutlineHeart,
} from "react-icons/hi";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { MouseEvent } from "react";
import { toggleActivityFavorite } from "ui/utils/toggle-activity-favorite";
import { useWindowWidth } from "ui/utils/hooks";
import { VisualSupport } from "domain/entities/visual-supports.entities";
import { useDisclosure } from "@chakra-ui/hooks";
import { Popover, PopoverContent, PopoverTrigger } from "@chakra-ui/react";
import { Snackbar } from "ui/pages/snackbar";
import { useNavigate } from "react-router-dom";
import { selectFavoriteVisualSupports } from "domain/activities/activities.selectors";
import { getDownloadLink } from "domain/activities/activities.actions";
import { CustomTextTooltip } from "ui/pages/app/components/CustomTextTooltip";
import { translate } from "ui/i18n";

interface IProps {
  support: VisualSupport;
  onClick: () => void;
  compact?: boolean;
}

export function VisualSupportCard({ support, onClick, compact }: IProps) {
  const isPremium = useAppSelector(selectIsPremium);
  const dispatch = useAppDispatch();
  const isDesktop = useWindowWidth();
  const popoverState = useDisclosure();
  const navigate = useNavigate();
  const favoriteSupport = useAppSelector(selectFavoriteVisualSupports).find(
    (t) => t.visuelId === support?.id
  );

  const handleCardClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (onClick) onClick();
  };

  const handleFavoriteClick = async (e: MouseEvent) => {
    e.stopPropagation();
    if (!isPremium && !!support.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(
        () => navigate("/account"),
        "visual-support"
      );
    } else {
      toggleActivityFavorite({
        favoriteActivity: favoriteSupport,
        dispatch,
        activityId: support.id,
        activityType: "visual-support",
      });
    }
  };

  const handleDownloadClick = async (e: MouseEvent, type: "pdf" | "png") => {
    e.stopPropagation();
    if (!isPremium && !!support.premium) {
      if (onClick) onClick();
    } else {
      const result = await dispatch(
        getDownloadLink({
          type: "visual",
          id: support.id,
          property: type,
        })
      ).unwrap();
      if (result.success && result.url) {
        window.open(result.url, "_blank").focus();
      }
    }
  };

  return (
    <div
      className={`flex h-full cursor-pointer flex-col justify-between rounded-[20px] bg-white p-3 ${
        onClick ? "cursor-pointer" : ""
      } ${compact ? "min-h-[270px]" : "md:p-5"}`}
      onClick={handleCardClick}
    >
      <div>
        <div className="relative rounded-[20px] bg-secondaryGrey-500">
          <div
            className={
              !isPremium && !!support.premium ? "opacity-30" : undefined
            }
          >
            <img
              src={`${support.thumbnail}?timestamp=${Date.now()}`}
              alt={"Support visuel " + support.title}
              width="100%"
              height="100%"
            />
          </div>

          <button
            className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white disabled:opacity-40 ${
              compact ? "" : "md:right-4 md:top-4"
            }`}
            onClick={handleFavoriteClick}
          >
            {!isPremium && !!support.premium ? (
              <HiLockClosed className="text-secondaryGrey-900" size={18} />
            ) : (
              <>
                {!!favoriteSupport?.id ? (
                  <HiHeart className="text-secondaryGrey-900" size={18} />
                ) : (
                  <HiOutlineHeart
                    className="text-secondaryGrey-900"
                    size={18}
                  />
                )}
              </>
            )}
          </button>
        </div>
        <span
          className={`mt-2 block text-sm font-bold md:text-base ${
            !isPremium && !!support.premium && "opacity-30"
          }`}
        >
          {support.title}
        </span>
      </div>

      <div className="mt-2 flex justify-end md:mt-4">
        <CustomTextTooltip
          text={translate("ns1:Activities.Tooltips.Download")}
          hide={popoverState.isOpen}
        >
          <div>
            <Popover
              isOpen={popoverState.isOpen}
              onOpen={popoverState.onOpen}
              onClose={popoverState.onClose}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                    popoverState.isOpen ? "bg-darkGrey-200" : "bg-darkGrey-800"
                  } ${compact ? "" : "md:h-11 md:w-11"}`}
                >
                  <HiDownload
                    size={isDesktop && !compact ? 24 : 20}
                    className={`${
                      popoverState.isOpen ? "text-darkGrey-700" : "text-white"
                    }`}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                style={{
                  isolation: "isolate",
                  zIndex: 100,
                  width: "fit-content",
                  border: "none",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
              >
                <div className="flex flex-col rounded-[20px] bg-white px-2 py-3 text-sm font-semibold text-darkGrey-700 shadow-lg">
                  <button
                    className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                    onClick={(e) => handleDownloadClick(e, "pdf")}
                  >
                    <HiDownload
                      className="me-2 text-secondaryGrey-900"
                      size={20}
                    />
                    Pdf
                  </button>
                  <button
                    className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                    onClick={(e) => handleDownloadClick(e, "png")}
                  >
                    <HiDownload
                      className="me-2 text-secondaryGrey-900"
                      size={20}
                    />
                    Png
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </CustomTextTooltip>
      </div>
    </div>
  );
}
